// components/Modals/ContentModal/ContentModal.js - UPDATED VERSION
import React, { useState, useEffect, useRef } from 'react';
import './ContentModal.css';
import { toast } from 'react-hot-toast';
import { useConfirmModal } from '../../../contexts/ConfirmModalContext';

// Tab Components
import MediaTab from './tabs/MediaTab';
import SocialMediaTab from './tabs/SocialMediaTab';
import AutogramSettingsTab from './tabs/AutogramSettingsTab';

// Other Components
import MediaWatermark from './components/MediaWatermark';
import VideoWatermark from './components/VideoWatermark';
import LoadingOverlay from './components/LoadingOverlay';

function ContentModal({
    isOpen,
    onClose,
    contentForm,
    setContentForm,
    onSubmit,
    editingContentId,
    buttonForm,
    setButtonForm,
    onDelete,
    tags = [],
    bulkMode = false,
    bulkContentIds = [],
    onBulkSubmit = null
}) {
    // Context hook
    const { showConfirm } = useConfirmModal();

    // Başlangıç form state'i (değişiklik kontrolü için)
    const initialFormRef = useRef(null);

    // Media states
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

    // UI states
    const [activeTab, setActiveTab] = useState('media');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form states
    const [currentButton, setCurrentButton] = useState({ text: '', url: '' });
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkData, setLinkData] = useState({ text: '', url: '' });

    // Watermark states
    const [showWatermark, setShowWatermark] = useState(false);
    const [watermarkingIndex, setWatermarkingIndex] = useState(null);


    // Video Watermark states
    const [showVideoWatermark, setShowVideoWatermark] = useState(false);
    const [videoWatermarkingIndex, setVideoWatermarkingIndex] = useState(null);

    // Platform-specific states
    const [telegramSettings, setTelegramSettings] = useState({
        buttons: [],
        parse_mode: 'HTML',
        disable_notification: false,
        protect_content: false
    });

    const [youtubeSettings, setYoutubeSettings] = useState({
        title: '',
        description: '',
        tags: [],
        privacy: 'private',
        categoryId: '22',
        shorts: true
    });

    const [xSettings, setXSettings] = useState({
        text: '',
        useOriginal: false,
        charCount: 0
    });

    const [tiktokSettings, setTiktokSettings] = useState({
        caption: '',
        hashtags: '',
        allowComments: true,
        allowDuet: true
    });

    const [instagramSettings, setInstagramSettings] = useState({
        contentType: 'post', // 'post', 'reel', 'story'
        caption: '',
        firstComment: '',
        collaborators: '',
        userTags: ''
    });

    const [platformSettings, setPlatformSettings] = useState({
        telegram: false,
        youtube: false,
        x: false,
        tiktok: false,
        instagram: false
    });

    const [localTags, setLocalTags] = useState(tags);

    // Helper function to parse existing media
    const parseExistingMedia = () => {
        try {
            if (!contentForm) return [];

            let media = [];

            // media_type'ı parse et
            let mediaTypes = [];
            if (contentForm.media_type) {
                try {
                    const parsed = JSON.parse(contentForm.media_type);
                    mediaTypes = Array.isArray(parsed) ? parsed : [parsed];
                } catch (e) {
                    mediaTypes = [contentForm.media_type];
                }
            }

            // media_path'i parse et
            let mediaPaths = [];
            if (contentForm.media_path) {
                try {
                    const parsed = JSON.parse(contentForm.media_path);
                    mediaPaths = Array.isArray(parsed) ? parsed : [parsed];
                } catch (e) {
                    mediaPaths = [contentForm.media_path];
                }
            }

            // Media objelerini oluştur
            for (let i = 0; i < mediaPaths.length; i++) {
                // Sadece filename'i al, "uploads/" prefix'i kaldır
                const filename = mediaPaths[i];

                media.push({
                    url: `uploads/${filename}`,
                    filename: filename,
                    type: mediaTypes[i] || 'unknown',
                    mimetype: `${mediaTypes[i]}/unknown`
                });
            }

            console.log('📦 Parsed existing media:', media);
            return media;
        } catch (error) {
            console.error('❌ existingMedia parse error:', error);
            return [];
        }
    };

    useEffect(() => {
        setLocalTags(tags);
    }, [tags]);

    // Modal açıldığında başlangıç state'ini kaydet
    useEffect(() => {
        if (isOpen) {
            // Biraz gecikme ile state'lerin yüklenmesini bekle
            setTimeout(() => {
                initialFormRef.current = JSON.stringify({
                    title: contentForm?.title || '',
                    description: contentForm?.description || '',
                    selectedTagIds: selectedTagIds,
                    mediaFilesCount: mediaFiles.length,
                    platformSettings: platformSettings,
                    xText: xSettings.text,
                    tiktokCaption: tiktokSettings.caption,
                    instagramCaption: instagramSettings.caption,
                    youtubeTitle: youtubeSettings.title
                });
            }, 500);
        } else {
            initialFormRef.current = null;
        }
    }, [isOpen, editingContentId]);

    // Form değişti mi kontrol et
    const hasFormChanged = () => {
        if (!initialFormRef.current) return false;

        const current = JSON.stringify({
            title: contentForm?.title || '',
            description: contentForm?.description || '',
            selectedTagIds: selectedTagIds,
            mediaFilesCount: mediaFiles.length,
            platformSettings: platformSettings,
            xText: xSettings.text,
            tiktokCaption: tiktokSettings.caption,
            instagramCaption: instagramSettings.caption,
            youtubeTitle: youtubeSettings.title
        });

        return current !== initialFormRef.current;
    };

    // Modal kapatma (değişiklik kontrolü ile)
    const handleClose = () => {
        if (hasFormChanged()) {
            showConfirm(async () => {
                onClose();
            });
        } else {
            onClose();
        }
    };

    // Initialize states from contentForm - ONLY on modal open
    useEffect(() => {
        if (isOpen && contentForm) {
            console.log('📥 Modal açıldı, contentForm:', contentForm);
            console.log('📥 contentForm.x_text:', contentForm.x_text);

            // Tags - sadece modal açıldığında yükle
            if (contentForm.tagIds && contentForm.tagIds.length > 0) {
                setSelectedTagIds(contentForm.tagIds);
            } else if (contentForm.content_tags && contentForm.content_tags.length > 0) {
                const ids = contentForm.content_tags.map(tag => tag.id);
                setSelectedTagIds(ids);
            }

            // Telegram settings
            if (contentForm.buttons) {
                setTelegramSettings(prev => ({
                    ...prev,
                    buttons: contentForm.buttons
                }));
                setButtonForm(contentForm.buttons);
            }

            // YouTube settings
            if (editingContentId && (contentForm.youtube_title || contentForm.youtube_description)) {
                setYoutubeSettings({
                    title: contentForm.youtube_title || '',
                    description: contentForm.youtube_description || '',
                    tags: contentForm.youtube_tags || [],
                    privacy: contentForm.youtube_privacy || 'public',
                    categoryId: contentForm.youtube_category_id || '24',
                    shorts: true
                });
                setPlatformSettings(prev => ({ ...prev, youtube: true }));
            }

            // X settings - HER ZAMAN KONTROL ET
            console.log('📥 contentForm.x_text kontrolü:', {
                x_text: contentForm.x_text,
                x_text_type: typeof contentForm.x_text,
                x_text_exists: contentForm.x_text !== undefined && contentForm.x_text !== null,
                x_text_empty: contentForm.x_text === ''
            });

            // TikTok settings
            if (editingContentId && (contentForm.tiktok_caption || contentForm.tiktok_hashtags)) {
                setTiktokSettings({
                    caption: contentForm.tiktok_caption || '',
                    hashtags: contentForm.tiktok_hashtags || '',
                    allowComments: contentForm.tiktok_allow_comments !== false,
                    allowDuet: contentForm.tiktok_allow_duet !== false
                });
                setPlatformSettings(prev => ({ ...prev, tiktok: true }));
            }

            // TikTok enabled kontrolü
            if (contentForm.is_tiktok_enabled) {
                setPlatformSettings(prev => ({ ...prev, tiktok: true }));
            }

            if (contentForm.x_text !== undefined && contentForm.x_text !== null && contentForm.x_text !== '') {
                console.log('📥 X text yükleniyor:', contentForm.x_text);
                setXSettings({
                    text: contentForm.x_text,
                    useOriginal: false,
                    charCount: String(contentForm.x_text).length
                });
                setPlatformSettings(prev => ({ ...prev, x: true }));
            } else {
                console.log('📥 X text boş veya undefined, platform state kontrol ediliyor');
                // is_x_enabled true ise bile text boş olabilir
                if (contentForm.is_x_enabled) {
                    setPlatformSettings(prev => ({ ...prev, x: true }));
                    setXSettings({
                        text: '',
                        useOriginal: false,
                        charCount: 0
                    });
                }
            }

            if (editingContentId && (contentForm.instagram_caption || contentForm.instagram_content_type)) {
                setInstagramSettings({
                    contentType: contentForm.instagram_content_type || 'post',
                    caption: contentForm.instagram_caption || '',
                    firstComment: contentForm.instagram_first_comment || '',
                    collaborators: contentForm.instagram_collaborators || '',
                    userTags: contentForm.instagram_user_tags || ''
                });

                if (contentForm.is_instagram_enabled) {
                    setPlatformSettings(prev => ({ ...prev, instagram: true }));
                }
            }

            // Platform settings
            if (contentForm.platforms) {
                setPlatformSettings(contentForm.platforms);
            }
        }
    }, [isOpen, editingContentId, contentForm]);

    // Ensure buttonForm is array
    useEffect(() => {
        if (!Array.isArray(buttonForm)) {
            setButtonForm([]);
        }
    }, [buttonForm, setButtonForm]);

    // Clean up on modal close
    useEffect(() => {
        if (!isOpen) {
            // Clean up media previews
            mediaPreviews.forEach(preview => {
                if (preview.type === 'video' && preview.url?.startsWith('blob:')) {
                    URL.revokeObjectURL(preview.url);
                }
            });

            // Reset all states
            setMediaPreviews([]);
            setMediaFiles([]);
            setActiveTab('media');
            setShowEmojiPicker(false);
            setCurrentButton({ text: '', url: '' });
            setSelectedTagIds([]);
            setShowLinkModal(false);
            setLinkData({ text: '', url: '' });
            setShowWatermark(false);
            setWatermarkingIndex(null);
            setUploadProgress(0);
            setIsUploading(false);
            setIsSubmitting(false);
            setTelegramSettings({
                buttons: [],
                parse_mode: 'HTML',
                disable_notification: false,
                protect_content: false
            });
            setYoutubeSettings({
                title: '',
                description: '',
                tags: [],
                privacy: 'private',
                categoryId: '22',
                shorts: true
            });
            setXSettings({
                text: '',
                useOriginal: false,
                charCount: 0
            });
            setTiktokSettings({
                caption: '',
                hashtags: '',
                allowComments: true,
                allowDuet: true
            });
            setInstagramSettings({
                contentType: 'post',
                caption: '',
                firstComment: '',
                collaborators: '',
                userTags: ''
            });
            setPlatformSettings({
                telegram: true,
                youtube: false,
                x: false,
                tiktok: false,
                instagram: false
            });
        }
    }, [isOpen, mediaPreviews]);

    // ESC key handler
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27) {
                if (showLinkModal) {
                    setShowLinkModal(false);
                } else if (showEmojiPicker) {
                    setShowEmojiPicker(false);
                } else if (showWatermark) {
                    setShowWatermark(false);
                } else if (showVideoWatermark) {
                    setShowVideoWatermark(false);
                } else if (isOpen) {
                    handleClose();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, showEmojiPicker, showLinkModal, showWatermark, contentForm, selectedTagIds, mediaFiles, platformSettings, xSettings, tiktokSettings, instagramSettings, youtubeSettings]);

    useEffect(() => {
        if (isOpen && bulkMode) {
            setActiveTab('social');
        }
    }, [isOpen, bulkMode]);

    const handleTagCreated = (newTag) => {
        setLocalTags(prevTags => [...prevTags, newTag]);
    };

    // Watermark handlers
    const handleWatermarkSave = (processedFile, processedPreview) => {
        if (watermarkingIndex !== null) {
            const newMediaFiles = [...mediaFiles];
            const newMediaPreviews = [...mediaPreviews];

            // Mevcut medya index'ini hesapla
            const existingMediaCount = mediaPreviews.filter(p => p.isExisting).length;
            const wasExisting = newMediaPreviews[watermarkingIndex]?.isExisting;

            if (wasExisting) {
                // Mevcut medyayı yeni dosya olarak ekle
                // mediaFiles array'inde doğru pozisyona ekle
                const newFileIndex = watermarkingIndex - (existingMediaCount - 1);
                if (newFileIndex >= 0) {
                    newMediaFiles.splice(newFileIndex, 0, processedFile);
                } else {
                    newMediaFiles.unshift(processedFile);
                }
            } else {
                newMediaFiles[watermarkingIndex] = processedFile;
            }

            newMediaPreviews[watermarkingIndex] = {
                ...newMediaPreviews[watermarkingIndex],
                url: processedPreview,
                watermarked: true,
                isExisting: false,
                file: processedFile
            };

            setMediaFiles(newMediaFiles);
            setMediaPreviews(newMediaPreviews);
            setContentForm({ ...contentForm, media: newMediaFiles });
        }

        setShowWatermark(false);
        setWatermarkingIndex(null);
        toast.success('Watermark başarıyla eklendi!');
    };

    const handleOpenWatermark = (index) => {
        const preview = mediaPreviews[index];
        if (preview && preview.type === 'image') {
            setWatermarkingIndex(index);
            setShowWatermark(true);
        }
    };

    const handleOpenVideoWatermark = (index) => {
        const preview = mediaPreviews[index];
        if (preview && preview.type === 'video') {
            setVideoWatermarkingIndex(index);
            setShowVideoWatermark(true);
        }
    };

    const handleVideoWatermarkSave = async (watermarkData) => {
        if (videoWatermarkingIndex !== null) {
            const newMediaPreviews = [...mediaPreviews];
            const newMediaFiles = [...mediaFiles];
            const currentPreview = newMediaPreviews[videoWatermarkingIndex];

            // Mevcut video ise, sunucudan indir ve File objesine çevir
            if (currentPreview?.isExisting) {
                try {
                    const videoUrl = currentPreview.url;
                    console.log('🎬 Mevcut video indiriliyor:', videoUrl);

                    const response = await fetch(videoUrl);
                    const blob = await response.blob();

                    const fileName = currentPreview.name || `video_${Date.now()}.mp4`;
                    const videoFile = new File([blob], fileName, {
                        type: blob.type || 'video/mp4',
                        lastModified: Date.now()
                    });

                    // mediaFiles'a ekle
                    newMediaFiles[videoWatermarkingIndex] = videoFile;
                    setMediaFiles(newMediaFiles);

                    console.log('✅ Video dosyası oluşturuldu:', fileName);
                } catch (error) {
                    console.error('❌ Video indirme hatası:', error);
                    toast.error('Video işlenirken hata oluştu');
                    return;
                }
            }

            // Watermark data'yı preview'a ekle
            newMediaPreviews[videoWatermarkingIndex] = {
                ...newMediaPreviews[videoWatermarkingIndex],
                watermarked: true,
                isExisting: false,
                videoWatermarkData: watermarkData
            };

            setMediaPreviews(newMediaPreviews);

            // ContentForm'a da ekle (backend'e gönderilecek)
            setContentForm(prev => ({
                ...prev,
                media: newMediaFiles,
                videoWatermarks: {
                    ...(prev.videoWatermarks || {}),
                    [videoWatermarkingIndex]: watermarkData
                }
            }));
        }

        setShowVideoWatermark(false);
        setVideoWatermarkingIndex(null);
        toast.success('Video logo ayarları kaydedildi!');
    };

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (bulkMode && onBulkSubmit) {
            setIsSubmitting(true);
            try {
                const updates = {};

                // Sadece doldurulan alanları topla
                if (contentForm.title?.trim()) {
                    updates.title = contentForm.title.trim();
                }

                if (contentForm.content?.trim()) {
                    updates.content = contentForm.content.trim();
                }

                // Butonlar
                if (telegramSettings.buttons && telegramSettings.buttons.length > 0) {
                    // buttonForm'u kontrol et - gerçekten buton var mı
                    const hasValidButtons = telegramSettings.buttons.some(row => {
                        if (Array.isArray(row)) {
                            return row.some(btn => btn && btn.text && btn.url);
                        }
                        return row && row.text && row.url;
                    });
                    if (hasValidButtons) {
                        updates.buttons = typeof telegramSettings.buttons === 'string'
                            ? telegramSettings.buttons
                            : JSON.stringify(telegramSettings.buttons);
                    }
                }

                // Platform ayarları
                if (platformSettings.x) {
                    updates.is_x_enabled = true;
                    if (xSettings.text?.trim()) {
                        updates.x_text = xSettings.text.trim();
                    }
                }

                if (platformSettings.tiktok) {
                    updates.is_tiktok_enabled = true;
                    if (tiktokSettings.caption?.trim()) updates.tiktok_caption = tiktokSettings.caption.trim();
                    if (tiktokSettings.hashtags?.trim()) updates.tiktok_hashtags = tiktokSettings.hashtags.trim();
                    updates.tiktok_allow_comments = tiktokSettings.allowComments;
                    updates.tiktok_allow_duet = tiktokSettings.allowDuet;
                }

                if (platformSettings.instagram) {
                    updates.is_instagram_enabled = true;
                    if (instagramSettings.caption?.trim()) updates.instagram_caption = instagramSettings.caption.trim();
                    if (instagramSettings.contentType) updates.instagram_content_type = instagramSettings.contentType;
                    if (instagramSettings.firstComment?.trim()) updates.instagram_first_comment = instagramSettings.firstComment.trim();
                    if (instagramSettings.collaborators?.trim()) updates.instagram_collaborators = instagramSettings.collaborators.trim();
                    if (instagramSettings.userTags?.trim()) updates.instagram_user_tags = instagramSettings.userTags.trim();
                }

                if (platformSettings.youtube) {
                    if (youtubeSettings.title?.trim()) updates.youtube_title = youtubeSettings.title.trim();
                    if (youtubeSettings.description?.trim()) updates.youtube_description = youtubeSettings.description.trim();
                }

                // Etiketler - ekle/kaldır modunda
                if (selectedTagIds.length > 0) {
                    updates.addTagIds = selectedTagIds;
                }

                if (Object.keys(updates).length === 0) {
                    toast.error('En az bir alan doldurmalısınız');
                    setIsSubmitting(false);
                    return;
                }

                await onBulkSubmit(bulkContentIds, updates);
                setIsSubmitting(false);
                onClose();
            } catch (error) {
                console.error('Bulk submit hatası:', error);
                toast.error('Toplu güncelleme hatası: ' + (error.message || 'Bilinmeyen hata'));
                setIsSubmitting(false);
            }
            return; // Normal akışa devam etme
        }



        // Debug logları
        console.log('🔍 Submit öncesi xSettings:', xSettings);
        console.log('🔍 X platformu seçili mi?:', platformSettings.x);

        if (!contentForm.title?.trim()) {
            toast.error('Başlık zorunludur');
            return;
        }

        if (selectedTagIds.length === 0) {
            toast.error('En az bir etiket seçmelisiniz');
            return;
        }

        // En az bir platform seçili olmalı
        const hasSelectedPlatform = Object.values(platformSettings).some(v => v);
        if (!hasSelectedPlatform) {
            toast.error('En az bir platform seçmelisiniz');
            return;
        }

        setIsSubmitting(true);

        try {
            const cleanTagIds = selectedTagIds.map(id => {
                if (typeof id === 'string') {
                    return parseInt(id.replace(/[\[\]"']/g, ''));
                }
                return parseInt(id);
            }).filter(id => !isNaN(id));

            // Girls etiketi kontrolü - girls etiketi yoksa fallback text gönderilmez
            const hasGirlsTag = localTags.some(tag =>
                cleanTagIds.includes(tag.id) &&
                tag.name.toLowerCase().includes('girls')
            );

            console.log('🏷️ Girls etiketi kontrolü:', hasGirlsTag ? 'VAR' : 'YOK');

            // Video watermark bilgilerini topla
            const videoWatermarksData = {};
            let watermarkLogoFile = null;

            mediaPreviews.forEach((preview, index) => {
                if (preview.videoWatermarkData) {
                    videoWatermarksData[index] = {
                        position: preview.videoWatermarkData.position,
                        size: preview.videoWatermarkData.size,
                        opacity: preview.videoWatermarkData.opacity,
                        customX: preview.videoWatermarkData.customX,
                        customY: preview.videoWatermarkData.customY
                    };
                    // Logo dosyasını al
                    if (preview.videoWatermarkData.file && !watermarkLogoFile) {
                        watermarkLogoFile = preview.videoWatermarkData.file;
                    }
                }
            });

            const updatedForm = {
                ...contentForm,
                tagIds: cleanTagIds,
                media: mediaFiles,
                platforms: platformSettings,

                // Video Watermark bilgileri
                videoWatermarks: Object.keys(videoWatermarksData).length > 0 ? videoWatermarksData : null,
                watermarkLogoFile: watermarkLogoFile,

                // Telegram settings
                buttons: platformSettings.telegram ? telegramSettings.buttons : [],
                telegram_settings: platformSettings.telegram ? telegramSettings : null,

                // X settings - girls etiketi yoksa ve kullanıcı yazmamışsa boş gönder
                x_text: xSettings.text || (hasGirlsTag ? (contentForm.content || '') : ''),
                is_x_enabled: platformSettings.x,

                // Tiktok settings - girls etiketi yoksa ve kullanıcı yazmamışsa boş gönder
                tiktok_caption: platformSettings.tiktok ? (tiktokSettings.caption || (hasGirlsTag ? (contentForm.content || '') : '')) : null,
                tiktok_hashtags: platformSettings.tiktok ? tiktokSettings.hashtags : null,
                tiktok_allow_comments: platformSettings.tiktok ? tiktokSettings.allowComments : null,
                tiktok_allow_duet: platformSettings.tiktok ? tiktokSettings.allowDuet : null,
                is_tiktok_enabled: platformSettings.tiktok,

                // Instagram settings - girls etiketi yoksa ve kullanıcı yazmamışsa boş gönder
                instagram_caption: platformSettings.instagram ? (instagramSettings.caption || (hasGirlsTag ? (contentForm.content || '') : '')) : null,
                instagram_content_type: platformSettings.instagram ? instagramSettings.contentType : null,
                instagram_first_comment: platformSettings.instagram ? instagramSettings.firstComment : null,
                instagram_collaborators: platformSettings.instagram ? instagramSettings.collaborators : null,
                instagram_user_tags: platformSettings.instagram ? instagramSettings.userTags : null,
                is_instagram_enabled: platformSettings.instagram,

                // YouTube settings
                youtube_title: platformSettings.youtube ? youtubeSettings.title : null,
                youtube_description: platformSettings.youtube ? youtubeSettings.description : null,
                youtube_tags: platformSettings.youtube ? youtubeSettings.tags : null,
                youtube_privacy: platformSettings.youtube ? youtubeSettings.privacy : null,
                youtube_category_id: platformSettings.youtube ? youtubeSettings.categoryId : null,
                is_youtube_enabled: platformSettings.youtube,
                is_youtube_shorts: platformSettings.youtube && youtubeSettings.shorts
            };

            // Debug logları
            console.log('📤 Gönderilecek form:', updatedForm);
            console.log('📤 Video watermarks:', videoWatermarksData);
            console.log('📤 Watermark logo file:', watermarkLogoFile ? 'VAR' : 'YOK');

            if (typeof onSubmit === 'function') {
                const result = await onSubmit(updatedForm);

                if (result !== false) {
                    setIsSubmitting(false);
                    onClose();

                    // Platform bazlı mesaj
                    const activePlatforms = Object.entries(platformSettings)
                        .filter(([_, active]) => active)
                        .map(([platform]) => platform);

                    const platformNames = {
                        telegram: 'Telegram',
                        youtube: 'YouTube Shorts',
                        x: 'X (Twitter)',
                        tiktok: 'TikTok',
                        instagram: 'Instagram'
                    };

                    const platformList = activePlatforms.map(p => platformNames[p]).join(', ');

                    toast.success(
                        editingContentId ?
                            `İçerik güncellendi! (${platformList})` :
                            `İçerik eklendi! (${platformList})`
                    );
                } else {
                    setIsSubmitting(false);
                }
            } else {
                console.error('onSubmit fonksiyonu tanımlı değil!');
                setIsSubmitting(false);
            }

        } catch (error) {
            console.error('Submit hatası:', error);
            toast.error('Bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
            setIsSubmitting(false);
        }
    };

    // Delete handler
    const handleDelete = () => {
        toast((t) => (
            <div>
                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    Bu içeriği silmek istediğinize emin misiniz?
                </p>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                    ⚠️ Bu işlem geri alınamaz!
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={() => {
                            toast.dismiss(t.id);
                            onDelete(editingContentId);
                            toast.success('İçerik silindi');
                        }}
                        style={{
                            padding: '8px 16px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        Evet, Sil
                    </button>
                    <button
                        onClick={() => toast.dismiss(t.id)}
                        style={{
                            padding: '8px 16px',
                            background: '#6B7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        İptal
                    </button>
                </div>
            </div>
        ), {
            duration: Infinity,
            position: 'top-center',
            style: {
                minWidth: '350px',
                background: '#FFF',
                color: '#000',
                padding: '20px'
            }
        });
    };

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && e.target.className === 'content-modal-backdrop') {
            handleClose();
        }
    };

    // Platform seçilme sayısını hesapla
    const selectedPlatformCount = Object.values(platformSettings).filter(v => v).length;

    return (
        <>
            <LoadingOverlay
                isLoading={isSubmitting}
                uploadProgress={uploadProgress}
                editingContentId={editingContentId}
                mediaFiles={mediaFiles}
            />

            {showWatermark && watermarkingIndex !== null && (
                <MediaWatermark
                    isOpen={showWatermark}
                    onClose={() => {
                        setShowWatermark(false);
                        setWatermarkingIndex(null);
                    }}
                    mediaFile={mediaFiles[watermarkingIndex]}
                    mediaPreview={mediaPreviews[watermarkingIndex]?.url}
                    onSave={handleWatermarkSave}
                />
            )}

            {showVideoWatermark && videoWatermarkingIndex !== null && (
                <VideoWatermark
                    isOpen={showVideoWatermark}
                    onClose={() => {
                        setShowVideoWatermark(false);
                        setVideoWatermarkingIndex(null);
                    }}
                    mediaFile={mediaFiles[videoWatermarkingIndex]}
                    mediaPreview={mediaPreviews[videoWatermarkingIndex]}
                    onSave={handleVideoWatermarkSave}
                />
            )}

            <div className="content-modal-backdrop" onMouseDown={handleBackdropClick}>
                <div className="content-modal-container" onMouseDown={(e) => e.stopPropagation()}>
                    {/* HEADER */}
                    <div className="content-modal-header">
                        <h2>
                            {bulkMode ?
                                <><span>📦</span> Toplu Düzenle ({bulkContentIds.length} içerik)</> :
                                editingContentId ?
                                    <><span>📝</span> İçeriği Düzenle</> :
                                    <><span>➕</span> Yeni İçerik Ekle</>
                            }
                        </h2>
                        <button className="modal-close-btn" onClick={handleClose}>✕</button>
                    </div>

                    {/* TAB NAVIGATION */}
                    <div className="content-modal-tabs">
                        {!bulkMode && (
                            <button
                                className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
                                onClick={() => setActiveTab('media')}
                            >
                                <span>🖼️</span> Medya
                                {mediaPreviews.length > 0 && <span className="badge">{mediaPreviews.length}</span>}
                            </button>
                        )}
                        <button
                            className={`tab-btn ${activeTab === 'social' ? 'active' : ''}`}
                            onClick={() => setActiveTab('social')}
                        >
                            <span>📱</span> Sosyal Medya
                            {selectedPlatformCount > 0 && <span className="badge">{selectedPlatformCount}</span>}
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'autogram' ? 'active' : ''}`}
                            onClick={() => setActiveTab('autogram')}
                        >
                            <span>⚙️</span> Autogram Ayarları
                            {selectedTagIds.length > 0 && <span className="badge">{selectedTagIds.length}</span>}
                        </button>
                    </div>

                    {/* MODAL BODY */}
                    <div className="content-modal-body">
                        <form onSubmit={handleSubmit} id="content-form">
                            {bulkMode && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                                    border: '1px solid #667eea40',
                                    borderRadius: '12px',
                                    padding: '16px 20px',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <span style={{ fontSize: '24px' }}>📦</span>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#4a5568', marginBottom: '4px' }}>
                                            Toplu Düzenleme Modu
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#718096' }}>
                                            Sadece doldurduğunuz alanlar {bulkContentIds.length} içeriğe uygulanacak. Boş bırakılan alanlar değiştirilmeyecek.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'media' && !bulkMode && (
                                <MediaTab
                                    mediaPreviews={mediaPreviews}
                                    setMediaPreviews={setMediaPreviews}
                                    mediaFiles={mediaFiles}
                                    setMediaFiles={setMediaFiles}
                                    contentForm={contentForm}
                                    setContentForm={setContentForm}
                                    selectedMediaIndex={selectedMediaIndex}
                                    setSelectedMediaIndex={setSelectedMediaIndex}
                                    handleOpenWatermark={handleOpenWatermark}
                                    handleOpenVideoWatermark={handleOpenVideoWatermark}
                                    isEditMode={!!editingContentId}
                                    existingMedia={parseExistingMedia()}
                                />
                            )}

                            {activeTab === 'social' && (
                                <SocialMediaTab
                                    platformSettings={platformSettings}
                                    setPlatformSettings={setPlatformSettings}
                                    telegramSettings={telegramSettings}
                                    setTelegramSettings={setTelegramSettings}
                                    youtubeSettings={youtubeSettings}
                                    setYoutubeSettings={setYoutubeSettings}
                                    buttonForm={buttonForm}
                                    setButtonForm={setButtonForm}
                                    currentButton={currentButton}
                                    setCurrentButton={setCurrentButton}
                                    contentForm={contentForm}
                                    setContentForm={setContentForm}
                                    showEmojiPicker={showEmojiPicker}
                                    setShowEmojiPicker={setShowEmojiPicker}
                                    showLinkModal={showLinkModal}
                                    setShowLinkModal={setShowLinkModal}
                                    linkData={linkData}
                                    setLinkData={setLinkData}
                                    mediaPreviews={mediaPreviews}
                                    isEditMode={!!editingContentId}
                                    existingMedia={parseExistingMedia()}
                                    xSettings={xSettings}
                                    setXSettings={setXSettings}
                                    tiktokSettings={tiktokSettings}
                                    setTiktokSettings={setTiktokSettings}
                                    instagramSettings={instagramSettings}
                                    setInstagramSettings={setInstagramSettings}
                                    selectedTagIds={selectedTagIds}
                                    tags={localTags}
                                    bulkMode={bulkMode}
                                />
                            )}

                            {activeTab === 'autogram' && (
                                <AutogramSettingsTab
                                    contentForm={contentForm}
                                    setContentForm={setContentForm}
                                    selectedTagIds={selectedTagIds}
                                    setSelectedTagIds={setSelectedTagIds}
                                    tags={localTags}
                                    onTagCreated={handleTagCreated}
                                />
                            )}
                        </form>
                    </div>

                    {/* MODAL FOOTER */}
                    <div className="content-modal-footer">
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            {editingContentId && !bulkMode && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="btn-delete"
                                >
                                    <span>🗑️</span> İçeriği Sil
                                </button>
                            )}

                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                marginLeft: editingContentId ? '0' : 'auto'
                            }}>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="btn-cancel"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    form="content-form"
                                    className="btn-submit"
                                    disabled={isSubmitting}
                                >
                                    {bulkMode ?
                                        <><span>📦</span> {isSubmitting ? 'Güncelleniyor...' : `${bulkContentIds.length} İçeriği Güncelle`}</> :
                                        editingContentId ?
                                            <><span>💾</span> Güncelle</> :
                                            <><span>➕</span> Kütüphaneye Ekle</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ContentModal;